/**
 * WorkingCapitalPage — Prescriptive Working Capital Action Engine (Feature 6).
 * Ranked recommendations with cash impact, type grouping, and action workflow.
 */

import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useForecastSummary, useWorkingCapital, useUpdateWorkingCapital } from './api';
import { formatCurrency, formatPct } from './utils';
import { cn } from '../../shared/lib/cn';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { WorkingCapitalRecommendation } from './types';

const TYPE_COLORS: Record<string, string> = {
  ar_acceleration: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  ap_extension: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  early_pay_discount: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  inventory_reduction: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  deposit_acceleration: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  payment_restructure: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
};

const CHART_COLORS: Record<string, string> = {
  ar_acceleration: '#10b981',
  ap_extension: '#3b82f6',
  early_pay_discount: '#8b5cf6',
  inventory_reduction: '#f59e0b',
  deposit_acceleration: '#06b6d4',
  payment_restructure: '#f43f5e',
};

type StatusFilter = 'all' | 'open' | 'actioned';

export default function WorkingCapitalPage() {
  const { data: summary } = useForecastSummary();
  const forecastId = summary?.forecast_id ?? '';
  const { data: recommendations, isLoading } = useWorkingCapital(forecastId);
  const updateWC = useUpdateWorkingCapital();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const filtered = useMemo(() => {
    if (!recommendations) return [];
    let items = [...recommendations];
    if (statusFilter !== 'all') {
      items = items.filter(r => r.status === statusFilter);
    }
    items.sort((a, b) => (a.priority_rank ?? 999) - (b.priority_rank ?? 999));
    return items;
  }, [recommendations, statusFilter]);

  const totalImpact = useMemo(
    () => (recommendations ?? []).reduce((s, r) => s + r.cash_impact, 0),
    [recommendations],
  );

  const impactByType = useMemo(() => {
    if (!recommendations?.length) return [];
    const byType = new Map<string, number>();
    for (const r of recommendations) {
      const t = r.recommendation_type || 'Other';
      byType.set(t, (byType.get(t) ?? 0) + r.cash_impact);
    }
    return Array.from(byType.entries())
      .map(([name, value]) => ({ name, value, fill: CHART_COLORS[name] || '#6b7280' }))
      .sort((a, b) => b.value - a.value);
  }, [recommendations]);

  if (isLoading || !forecastId) {
    return (
      <div className="space-y-6 mt-2">
        <Card>
          <CardContent className="p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-6 w-56 bg-gray-200 dark:bg-dark-surface-2 rounded" />
              <div className="grid grid-cols-2 gap-3">
                <div className="h-16 bg-gray-200 dark:bg-dark-surface-2 rounded" />
                <div className="h-16 bg-gray-200 dark:bg-dark-surface-2 rounded" />
              </div>
              <div className="h-60 bg-gray-200 dark:bg-dark-surface-2 rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!recommendations?.length) {
    return (
      <div className="space-y-6 mt-2">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100 mb-2">
              Working Capital Recommendations
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              No working capital recommendations available. Run the forecast engine to generate prescriptive actions.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-2">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Total Cash Impact</p>
            <p className={cn('text-2xl font-bold', totalImpact >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
              {formatCurrency(totalImpact, true)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Recommendations</p>
            <p className="text-2xl font-bold text-charcoal dark:text-gray-100">{recommendations.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Actioned</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {recommendations.filter(r => r.status === 'actioned').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Impact by Type Chart */}
      {impactByType.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-4">
              Impact by Recommendation Type
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={impactByType} layout="vertical" margin={{ left: 10, right: 20, top: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis
                  type="number"
                  tickFormatter={v => formatCurrency(v, true)}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={140}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                />
                <Tooltip
                  formatter={(v: number) => formatCurrency(v)}
                  contentStyle={{
                    backgroundColor: 'var(--color-surface, #fff)',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {impactByType.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Status Filter */}
      <div className="flex items-center gap-2">
        {(['all', 'open', 'actioned'] as StatusFilter[]).map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
              statusFilter === s
                ? 'bg-charcoal text-white dark:bg-gray-200 dark:text-gray-900'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-dark-surface-2 dark:text-gray-400 dark:hover:bg-dark-border',
            )}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)} {s !== 'all' ? `(${recommendations.filter(r => r.status === s).length})` : ''}
          </button>
        ))}
      </div>

      {/* Recommendation Cards */}
      <div className="space-y-3">
        {filtered.map((rec: WorkingCapitalRecommendation) => (
          <Card key={rec.id}>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {/* Priority Rank */}
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-charcoal dark:bg-gray-200 flex items-center justify-center">
                  <span className="text-sm font-bold text-white dark:text-gray-900">
                    #{rec.priority_rank ?? '—'}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={cn(
                      'px-2 py-0.5 rounded text-xs font-medium',
                      TYPE_COLORS[rec.recommendation_type] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
                    )}>
                      {rec.recommendation_type}
                    </span>
                    <span className={cn(
                      'px-2 py-0.5 rounded text-xs font-medium',
                      rec.status === 'actioned'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
                    )}>
                      {rec.status}
                    </span>
                  </div>

                  <div className="flex items-baseline gap-2 mb-1">
                    {rec.counterparty_name && (
                      <span className="text-sm font-semibold text-charcoal dark:text-gray-100">
                        {rec.counterparty_name}
                      </span>
                    )}
                    {rec.entity_name && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({rec.entity_name})
                      </span>
                    )}
                  </div>

                  {(rec.current_timing || rec.recommended_timing) && (
                    <div className="flex items-center gap-2 mb-2 text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{rec.current_timing ?? '—'}</span>
                      <span className="text-gray-400">→</span>
                      <span className="font-medium text-charcoal dark:text-gray-100">{rec.recommended_timing ?? '—'}</span>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                    {rec.rationale}
                  </p>

                  <div className="flex items-center gap-4 flex-wrap">
                    <span className={cn(
                      'text-lg font-bold',
                      rec.cash_impact >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
                    )}>
                      {rec.cash_impact >= 0 ? '+' : ''}{formatCurrency(rec.cash_impact, true)}
                    </span>
                    {rec.irr_annualized != null && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        IRR: {formatPct(rec.irr_annualized)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action Button */}
                {rec.status === 'open' && (
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => updateWC.mutate({ id: rec.id, status: 'actioned' })}
                      disabled={updateWC.isPending}
                      className="px-4 py-2 text-xs font-medium rounded-lg bg-charcoal text-white hover:bg-gray-800 dark:bg-gray-200 dark:text-gray-900 dark:hover:bg-gray-300 disabled:opacity-50 transition-colors"
                    >
                      Mark Actioned
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {filtered.length === 0 && (
          <Card>
            <CardContent className="p-6 text-center text-gray-500 dark:text-gray-400">
              No recommendations match the selected filter.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
